import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { AppError } from '@/lib/error';
import { rollbar } from '@/lib/rollbar';

import {
  AuthAdapter,
  AuthCapabilities,
  AuthFlowType,
  AuthState,
  AuthToken,
  AuthUser,
  CredentialsInput,
  InviteInfo,
  UserPreferences,
  VerifyPasswordResetInput,
} from './auth-adapter';

export type OIDCAdapterConfig = {
  /** The OIDC authority URL (issuer) */
  authority: string;
  /** The client ID registered with the OIDC provider */
  clientId: string;
  /** The redirect URI after successful authentication */
  redirectUri: string;
  /** The redirect URI after sign out (optional) */
  postLogoutRedirectUri?: string;
  /** OAuth scopes to request (defaults to 'openid profile email') */
  scopes?: string[];
  /** Enable silent token renewal (defaults to true) */
  automaticSilentRenew?: boolean;
  /** Silent renewal iframe timeout in milliseconds (defaults to 10000) */
  silentRequestTimeoutInSeconds?: number;
};

function mapOIDCUser(user: User): AuthUser {
  const profile = user.profile;

  return {
    id: profile.sub,
    email: profile.email,
    name: profile.name || profile.preferred_username,
    avatar: profile.picture,
    role: profile['role'] as string | undefined,
    permissions: (profile['permissions'] as string[] | undefined) || [],
    preferences: {},
    metadata: {
      emailVerified: profile.email_verified,
      locale: profile.locale,
      ...profile,
    },
  };
}

function mapOIDCToken(user: User): AuthToken {
  return {
    accessToken: user.access_token,
    refreshToken: user.refresh_token,
    idToken: user.id_token,
    expiresAt: user.expires_at ? new Date(user.expires_at * 1000) : undefined,
    tokenType: user.token_type || 'Bearer',
  };
}

function createAuthState(user: User | null, error: AppError | null = null): AuthState {
  if (error) {
    return {
      user: null,
      token: null,
      status: 'error',
      error,
    };
  }

  if (!user || user.expired) {
    return {
      user: null,
      token: null,
      status: 'unauthenticated',
      error: null,
    };
  }

  return {
    user: mapOIDCUser(user),
    token: mapOIDCToken(user),
    status: 'authenticated',
    error: null,
  };
}

export class OIDCAdapter extends AuthAdapter {
  readonly name = 'Task Masters';
  readonly flowType: AuthFlowType = 'redirect';
  readonly capabilities: AuthCapabilities = {
    canSignInWithCredentials: false,
    canSignUp: false,
    canResetPassword: false,
    canChangePassword: false,
    canAcceptInvite: false,
  };

  private refreshing = false;
  private userManager: UserManager;
  private currentState: AuthState;
  private listeners: Set<(state: AuthState) => void> = new Set();

  constructor(config: OIDCAdapterConfig) {
    super();

    const scopes = config.scopes?.join(' ') || 'openid profile email offline_access';

    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri || config.redirectUri,
      scope: scopes,
      response_type: 'code',
      automaticSilentRenew: config.automaticSilentRenew ?? true,
      silentRequestTimeoutInSeconds: config.silentRequestTimeoutInSeconds ?? 3,
      // Not ideal, but since we use rotating refresh tokens, each refresh is valid
      // for a short period of time, so storing it in the local storage is less of
      // a problem. See: https://auth0.com/blog/refresh-tokens-what-are-they-and-when-to-use-them/#You-Can-Store-Refresh-Token-In-Local-Storage
      userStore:
        typeof window !== 'undefined'
          ? new WebStorageStateStore({ store: window.localStorage })
          : undefined,
    });

    this.currentState = {
      user: null,
      token: null,
      status: 'idle',
      error: null,
    };

    this.setupEventListeners();

    // Make sure to bind the methods to avoid problem when called from outside the class
    this.initialize = this.initialize.bind(this);
    this.getAuthState = this.getAuthState.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.signIn = this.signIn.bind(this);
    this.signUp = this.signUp.bind(this);
    this.signOut = this.signOut.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
    this.verifyResetPasswordCode = this.verifyResetPasswordCode.bind(this);
    this.completePasswordReset = this.completePasswordReset.bind(this);
    this.changePassword = this.changePassword.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.resetErrors = this.resetErrors.bind(this);
  }

  private setupEventListeners(): void {
    // User loaded (after sign in or silent renew)
    this.userManager.events.addUserLoaded((user) => {
      this.updateState(createAuthState(user));
    });

    // User unloaded (after sign out)
    this.userManager.events.addUserUnloaded(() => {
      this.updateState(createAuthState(null));
    });

    // Silent renew error - user needs to re-authenticate
    this.userManager.events.addSilentRenewError((error) => {
      console.warn('Silent renew failed:', error);
      this.updateState(createAuthState(null));
    });

    // Access token expiring soon
    this.userManager.events.addAccessTokenExpiring(() => {
      console.debug('Access token expiring soon, silent renew should trigger');
    });

    // Access token expired
    this.userManager.events.addAccessTokenExpired(() => {
      // Avoid showing the login screen if we are already refreshing the token
      if (this.refreshing) return;
      console.debug('Access token expired');
      this.updateState(createAuthState(null));
    });
  }

  private updateState(newState: AuthState): void {
    this.currentState = newState;
    this.listeners.forEach((listener) => listener(newState));
  }

  async initialize(): Promise<void> {
    try {
      if (this.hasAuthParams()) {
        const user = await this.userManager.signinCallback();
        if (user) {
          this.updateState(createAuthState(user));
          return;
        }
      }

      this.updateState({
        ...this.currentState,
        status: 'loading',
      });

      // Try to get user from storage first
      const user = await this.userManager.getUser();

      if (user && !user.expired) {
        this.updateState(createAuthState(user));
        return;
      }

      // Try silent sign-in if no valid user in storage
      try {
        this.refreshing = true;
        const silentUser = await this.userManager.signinSilent();
        this.updateState(createAuthState(silentUser));
      } catch {
        // Silent sign-in failed, user needs to authenticate
        this.updateState(createAuthState(null));
      } finally {
        this.refreshing = false;
      }
    } catch (error) {
      rollbar.error('Failed to initialize OIDC adapter:', error as Error);
      this.updateState(
        createAuthState(
          null,
          error instanceof AppError
            ? error
            : new AppError('internalError', 'Initialization failed', { cause: error }),
        ),
      );
    }
  }

  getAuthState(): AuthState {
    return this.currentState;
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const user = await this.userManager.getUser();

      if (!user) {
        return null;
      }

      // If token is expired or about to expire, try to refresh
      if (user.expired) {
        try {
          const renewedUser = await this.userManager.signinSilent();
          return renewedUser?.access_token || null;
        } catch {
          return null;
        }
      }

      return user.access_token;
    } catch {
      return null;
    }
  }

  protected async signInWithRedirect(): Promise<void> {
    // Task Masters requires consent prompt for offline access
    await this.userManager.signinRedirect({ prompt: 'consent' });
  }

  async signOut(): Promise<void> {
    try {
      await this.userManager.signoutSilent();
    } catch (error) {
      // If silent fails, at least clear local state
      console.error('Sign out redirect failed:', error);
      await this.userManager.removeUser();
      this.updateState(createAuthState(null));
    }
  }

  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  protected signInWithCredentials(_credentials: CredentialsInput): Promise<AuthState> {
    throw new Error('Sign-in with credentials is not supported with OIDC adapter');
  }

  signUp(_credentials: CredentialsInput & { name?: string }): Promise<AuthState> {
    throw new Error('Sign-up is not supported by the OIDC adapter');
  }

  resetPassword(_email: string): Promise<void> {
    throw new Error('Password reset is not supported by the OIDC adapter');
  }

  verifyResetPasswordCode(_input: VerifyPasswordResetInput): Promise<string> {
    throw new Error('Verify code is not supported by the OIDC adapter');
  }

  completePasswordReset(_email: string, _code: string, _newPassword: string): Promise<void> {
    throw new Error('Reset password with userId is not supported by the OIDC adapter');
  }

  changePassword(_oldPassword: string, _newPassword: string): Promise<void> {
    throw new Error('Password change is not supported by the OIDC adapter.');
  }

  acceptInvite(_userId: string, _password: string, _email: string): Promise<void> {
    throw new Error('Accept invite is not supported by the OIDC adapter.');
  }

  checkInvite(_userId: string): Promise<InviteInfo> {
    throw new Error('Check invite is not supported by the OIDC adapter.');
  }

  async updatePreferences(_prefs: Partial<UserPreferences>): Promise<void> {
    // OIDC adapter has no Firestore backing; preferences are not persisted
  }

  resetErrors(): void {
    this.updateState({ ...this.currentState, error: null });
  }

  private hasAuthParams(): boolean {
    const searchParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;

    if (!searchParams) return false;
    return (
      (!!searchParams.get('code') || !!searchParams.get('error')) && !!searchParams.get('state')
    );
  }
}
